import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { configs } from '@/configs';
import { network } from '@/configs/network';
import type { InputExecuteSSO } from '@/libs/ssoWallet';
import { ActionSSO, ssoExecute } from '@/libs/ssoWallet';
import { useAuth } from '@/provider/UserProvider';
import { getMessageExecuteWithdrawWithCw20Token } from '@/utils/transactionMessage';
import { handleExecute } from '@/utils/wasm';

import { LOCK_CONTRACT_ADDRESS, NetworkKey } from '../constants';

const MESS_FINISH = 'Withdraw successfully!';

const useWithdrawWithOraiNetwork = (callback: (nonce: number) => void, nonce: number) => {
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [finish, setFinish] = useState('');

  const checkGas = (gas: number) => {
    if (gas > 4000000 || gas < 400000) {
      toast.error('Gas number must be between 400,000 and 4,000,000');
    }
  };

  const handleExecuteWithOwallet = async (message: string, additionalInfor: object) => {
    setLoading(true);
    const res = await handleExecute(
      LOCK_CONTRACT_ADDRESS[NetworkKey.Oraichain],
      auth.user.publicAddress,
      message,
      additionalInfor,
    );
    if (res?.tx_response?.txhash) {
      setLoading(false);
      setFinish(MESS_FINISH);
    }
  };

  const handleExecuteWithSSO = async (message: string) => {
    const input: InputExecuteSSO = {
      contractAddress: LOCK_CONTRACT_ADDRESS[NetworkKey.Oraichain],
      chainId: network?.chainId || 'Oraichain',
      params: message,
    };
    setLoading(true);
    await ssoExecute(input, (ActionSSO.withdrawToken + nonce) as ActionSSO);
  };

  const handleWithdraw = async ({
    fees,
    gas,
    nonce,
  }: {
    fees: number;
    gas: number;
    nonce: number;
  }) => {
    setErr('');
    setFinish('');
    checkGas(gas);
    const message = getMessageExecuteWithdrawWithCw20Token(nonce);
    const additionalInfor = {
      gas,
      fees,
      mode: 'BROADCAST_MODE_BLOCK',
    };

    try {
      if (auth.isOwallet) {
        await handleExecuteWithOwallet(message, additionalInfor);
      } else {
        await handleExecuteWithSSO(message);
      }
    } catch (error: any) {
      setLoading(false);
      console.log('claim err: ', error);
      toast.error(error?.message || 'An error occurred');
    }
  };

  // for sso
  useEffect(() => {
    if (typeof window !== 'undefined' && !auth.isOwallet) {
      window.addEventListener(
        'message',
        (event) => {
          if (event.origin !== configs.ssoUrl) return;
          const data = JSON.parse(event?.data || '');

          if (data.action !== ((ActionSSO.withdrawToken + nonce) as ActionSSO)) return;
          if (data.status !== 'success') {
            console.log('err withdraw airi: ', data.response);
            setErr(data.response);
          } else {
            setLoading(false);
            setFinish(MESS_FINISH);
          }
        },
        false,
      );
    }
    return window.removeEventListener('message', () => {});
  }, []);

  useEffect(() => {
    if (err) {
      toast.error(err);
    }
  }, [err]);

  useEffect(() => {
    if (finish) {
      if (auth.isOwallet) toast.success(finish);
      callback(nonce);
    }
  }, [finish, auth.isOwallet]);

  return { handleWithdraw, loading };
};

export default useWithdrawWithOraiNetwork;
